import { DashboardService } from 'data/services/dashboard';
import {
  TaskServiceInterface,
  AuthenticationServiceInterface,
  InternshipLocationsServiceInterface,
  LocationsServiceInterface,
  EvaluationCriteriaServiceInterface,
  QuestionsServiceInterface,
  QuestionOptionsServiceInterface,
  EntitiesServiceInterface,
  ProfessionsServiceInterface,
  ShiftsServiceInterface,
  InsuranceCompaniesServiceInterface,
  PersonServiceInterface,
  PushNotificationServiceInterface,
  ImpersonateServiceInterface,
  SearchByCnpjServiceInterface,
  ActivitiesServiceInterface,
  GroupsServiceInterface,
  ReportServiceInterface,
  CommonUtilsServiceInterface,
  AdvertisingServiceInterface,
  PartnersServiceInterface,
  ApiKeysServiceInterface,
  SsoProvidersServiceInterface,
} from 'domain/services';
import { CourseServiceInterface } from 'domain/services/course';
import { UsersServiceInterface } from 'domain/services/users';
import {
  makeInsuranceCompaniesService,
  makeInternshipLocationsService,
  makeLocationsService,
  makeImpersonateService,
  makePersonService,
  makePushNotificationService,
  makeQuestionOptionsService,
  makeQuestionsService,
  makeShiftsService,
  makeUsersService,
  makeActivitiesService,
  makeGroupsService,
  makeReportService,
  makeCommonUtilsService,
  makeAdvertisingService,
  makePartnersService,
  makeApiKeysService,
  makeSsoProvidersService,
} from 'main/factories/services';
import { makeTaskService } from 'main/factories/services';
import { makeAuthenticationService } from 'main/factories/services/authentication/authentication.service.factory';
import { makeCourseService } from 'main/factories/services/course/course.service.factory';
import { makeDashboardService } from 'main/factories/services/dashboard/dashboard.service.factory';
import { makeEntitiesService } from 'main/factories/services/entities';
import { makeEvaluationCriteriaService } from 'main/factories/services/evaluation-criteria/evaluation-criteria.service.factory';
import { makeProfessionsService } from 'main/factories/services/professions';
import { makeSearchBycNPJService } from 'main/factories/services/search-by-cnpj/search-by-cnpj.service.factory';
import { WithChildren } from 'presentation/config/helpers/react18MigrationHelpers';
import { FC, createContext, useContext, useState } from 'react';

type ServiceContextProps = {
  authenticationService: AuthenticationServiceInterface;
  courseService: CourseServiceInterface;
  usersService: UsersServiceInterface;
  taskService: TaskServiceInterface;
  dashboardService: DashboardService;
  internshipLocationsService: InternshipLocationsServiceInterface;
  locationsService: LocationsServiceInterface;
  evaluationCriteriaService: EvaluationCriteriaServiceInterface;
  questionsService: QuestionsServiceInterface;
  questionOptionsService: QuestionOptionsServiceInterface;
  entitiesService: EntitiesServiceInterface;
  professionsService: ProfessionsServiceInterface;
  shiftsService: ShiftsServiceInterface;
  insuranceCompaniesService: InsuranceCompaniesServiceInterface;
  personService: PersonServiceInterface;
  pushNotificationService: PushNotificationServiceInterface;
  impersonateService: ImpersonateServiceInterface;
  searchByCnpjService: SearchByCnpjServiceInterface;
  activityService: ActivitiesServiceInterface;
  groupsService: GroupsServiceInterface;
  reportService: ReportServiceInterface;
  commonUtilsService: CommonUtilsServiceInterface;
  advertisingService: AdvertisingServiceInterface;
  partnersService: PartnersServiceInterface;
  apiKeysService: ApiKeysServiceInterface;
  ssoProvidersService: SsoProvidersServiceInterface;
};

const initServiceContextPropsState = {
  authenticationService: makeAuthenticationService(),
  courseService: makeCourseService(),
  usersService: makeUsersService(),
  taskService: makeTaskService(),
  dashboardService: makeDashboardService(),
  internshipLocationsService: makeInternshipLocationsService(),
  locationsService: makeLocationsService(),
  evaluationCriteriaService: makeEvaluationCriteriaService(),
  questionsService: makeQuestionsService(),
  questionOptionsService: makeQuestionOptionsService(),
  entitiesService: makeEntitiesService(),
  professionsService: makeProfessionsService(),
  shiftsService: makeShiftsService(),
  insuranceCompaniesService: makeInsuranceCompaniesService(),
  personService: makePersonService(),
  pushNotificationService: makePushNotificationService(),
  impersonateService: makeImpersonateService(),
  searchByCnpjService: makeSearchBycNPJService(),
  activityService: makeActivitiesService(),
  groupsService: makeGroupsService(),
  reportService: makeReportService(),
  commonUtilsService: makeCommonUtilsService(),
  advertisingService: makeAdvertisingService(),
  partnersService: makePartnersService(),
  apiKeysService: makeApiKeysService(),
  ssoProvidersService: makeSsoProvidersService(),
};

const ServiceContext = createContext<ServiceContextProps>(
  initServiceContextPropsState,
);

const useService = () => {
  return useContext(ServiceContext);
};

const ServiceProvider: FC<WithChildren> = ({ children }) => {
  const [serviceContextProps] = useState(initServiceContextPropsState);
  return (
    <ServiceContext.Provider value={serviceContextProps}>
      {children}
    </ServiceContext.Provider>
  );
};

export { ServiceProvider, useService };
